import 'twin.macro';
import React, { Component, createRef } from 'react';
import clsx from 'clsx';
import Input from '@teetsh/app/src/components/Form/Input2';

export const safeParseFloat = (value: string | null | undefined): number => {
  if (value == null) {
    return NaN;
  }
  return parseFloat(value.replace(/,/g, '.'));
};

export type Props = {
  tabIndex?: number;
  className?: string;
  max: number;
  min: number;
  fractionDigits: number;
  emptyValue: string;
  id?: string;
  ['data-testid']?: string;
  'aria-label'?: string;
  value?: number;
  isInvalid?: boolean;
  onChange: (value?: number) => void;
  onBlur?: (value: number | undefined, isUpdate: boolean) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  step?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  error?: string;
  onKeyPress?: (v: any) => void;
  showInputOnHover?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  extraLarge?: boolean;
  large?: boolean;
  autoComplete?: 'off' | 'on';
  style?: object;
  selectAllOnFocus?: boolean;
  rightElement?: any;
};

type State = {
  lastValidValue: string;
  displayedValue: string;
  valueBeforeChange: number | undefined;
};

function getValidatedValue(
  targetValue: string,
  lastValidValue: string,
  min: number,
  max: number,
  fractionDigits: number,
): number | undefined {
  const parseableValue = Number.isNaN(safeParseFloat(targetValue))
    ? lastValidValue
    : targetValue;

  if (parseableValue) {
    const parsedValue = safeParseFloat(parseableValue);
    const roundedValue =
      Math.round(parsedValue * 10 ** fractionDigits) / 10 ** fractionDigits;

    return Math.min(Math.max(roundedValue, min), max);
  }

  return undefined;
}

// TODO should be refactored in FC
export default class NumberInput extends Component<Props, State> {
  // eslint-disable-next-line react/static-property-placement
  static defaultProps = {
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    fractionDigits: 2,
    emptyValue: '0',
  };

  inputRef = this.props.inputRef || createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    const { value: numberValue, emptyValue } = props;
    const value =
      numberValue !== undefined ? numberValue.toString() : emptyValue;

    this.state = {
      displayedValue: value,
      lastValidValue: value,
      valueBeforeChange: numberValue,
    };
  }

  componentDidMount() {
    const { autoFocus = false } = this.props;
    if (autoFocus && this.inputRef.current) {
      this.inputRef.current.select();
    }
  }

  componentWillUnmount() {
    const { onBlur } = this.props;
    const { valueBeforeChange } = this.state;
    if (this.inputRef.current && onBlur) {
      const { value } = this.inputRef.current;
      const validatedValue = getValidatedValue(
        value,
        this.state.lastValidValue,
        this.props.min || Number.MIN_SAFE_INTEGER,
        this.props.max || Number.MAX_SAFE_INTEGER,
        this.props.fractionDigits ?? 2,
      );

      const isUpdate = validatedValue !== valueBeforeChange;
      onBlur(validatedValue, isUpdate);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { value: newValue, autoFocus } = this.props;
    if (prevProps.value !== newValue) {
      const valueToSet = newValue == null ? '' : newValue.toString();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        displayedValue: valueToSet,
        lastValidValue: valueToSet,
      });
    } else if (!prevProps.autoFocus && autoFocus && this.inputRef.current) {
      this.inputRef.current.select();
    }
  }

  handleChange = ({
    target: {
      value,
      validity: { badInput },
    },
  }: any) => {
    const { emptyValue, onChange, value: propsValue } = this.props;
    this.setState(
      state => ({
        ...state,
        displayedValue: value,
        lastValidValue: badInput ? state.lastValidValue : value || emptyValue,
      }),
      () => {
        const validatedValue = getValidatedValue(
          value,
          this.state.lastValidValue,
          this.props.min || Number.MIN_SAFE_INTEGER,
          this.props.max || Number.MAX_SAFE_INTEGER,
          this.props.fractionDigits ?? 2,
        );
        if (validatedValue !== propsValue) {
          onChange(validatedValue);
        }
      },
    );
  };

  handleBlur = ({ target }: { target: HTMLInputElement }) => {
    const { onBlur, value: propsValue } = this.props;
    const { lastValidValue, valueBeforeChange } = this.state;
    const validatedValue = getValidatedValue(
      target.value,
      this.state.lastValidValue,
      this.props.min || Number.MIN_SAFE_INTEGER,
      this.props.max || Number.MAX_SAFE_INTEGER,
      this.props.fractionDigits ?? 2,
    );

    const isUpdate = propsValue !== valueBeforeChange;
    if (validatedValue !== undefined) {
      this.setState(
        state => ({
          ...state,
          valueBeforeChange: validatedValue,
          displayedValue: validatedValue.toString(),
          lastValidValue: validatedValue.toString(),
        }),
        () => onBlur && onBlur(validatedValue, isUpdate),
      );
    } else {
      this.setState(
        {
          valueBeforeChange: validatedValue,
          displayedValue: lastValidValue,
        },
        () => onBlur && onBlur(undefined, isUpdate),
      );
    }
  };

  render() {
    const { displayedValue } = this.state;
    const {
      id,
      step = 'any',
      isInvalid,
      'data-testid': testId,
      className,
      tabIndex,
      style,
      error,
      disabled,
      min,
      onKeyPress,
      onChange,
      value,
      fractionDigits,
      emptyValue,
      ...rest
    } = this.props;

    return (
      <Input
        style={style}
        aria-label={this.props['aria-label']}
        tabIndex={tabIndex}
        id={id}
        value={displayedValue}
        inputRef={this.inputRef}
        type="number"
        data-testid={testId || id}
        tw="text-right"
        className={clsx(className, {
          'is-invalid': isInvalid,
        })}
        onChange={this.handleChange}
        onClick={() => {
          if (this.inputRef.current && displayedValue === '0') {
            this.inputRef.current.select();
          }
        }}
        onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
          const { key, currentTarget } = event;
          if (key === 'Enter') {
            this.handleBlur({ ...event, target: currentTarget });
          } else if (key.match(/[a-zA-Z]/) || (min >= 0 && key.match(/[+-]/))) {
            event.preventDefault();
          }
          return onKeyPress && onKeyPress(event);
        }}
        step={step}
        min={min}
        {...rest}
        onBlur={this.handleBlur as any}
      />
    );
  }
}
